import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import useAppInitialization from "./hooks/useAppInitialization";
import { ToastContainer } from "react-toastify";
import { Toaster } from "react-hot-toast";
import "react-toastify/dist/ReactToastify.css";
import { ConfigProvider, Layout } from "antd";
import { ErrorBoundary } from "react-error-boundary";
import { useSelector, useDispatch } from "react-redux";
import { logout, setSessionExpired } from "./redux/auth/authSlice";
import PopupCard from "./components/Cards/PopupCard/PopupCard";
import Dashboard from "./pages/Dashboard/Dashboard";
import Projects from "./pages/Projects/ProjectsPage";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorPage from "./components/Pages/ErrorPage";
import EnvironmentBrsrReport from "./components/Form/ProjectsForm/EnvironmentBrsrReport/EnviromentBrsrReport";
import { ResetPassword } from "./pages/Auth";
import { SessionLogin } from "./pages/Auth/SessionLogin";
import { LoginAdmin } from "./pages/Auth/LoginAdmin";
import AdminRoutes from "./pages/adminRoutes";
import Group from "./pages/Group/Group";
import Customer from "./pages/Customer/Customer";
// import QuestionBank from './pages/QuestionBank/QuestionBank'
import QuestionBank from "./pages/QuestionsBank/QuestionBank";
import Questions from "./pages/QuestionsBank/Questions";
import StandardForms from "./pages/StandardForms/StandardForms";
import SupportTicket from "./pages/SupportTicket/SupportTicket";
import SupportAction from "./pages/SupportTicket/SupportAction/SupportAction";
import UserKYC from "./pages/UserKYC/UserKYC";
import Coupons from "./pages/Coupons/CouponsPage";
import Roles from "./pages/Roles/Roles";
import Users from "./pages/Users/Users";
import ProjectType from "./pages/ProjectType/ProjectsType";
import LicenceCost from "./pages/LicenceCost/LicenceCost";
import ReportType from "./pages/ReportType/ReportType";
import Report from "./pages/Report/Report";
import Profile from "./pages/Profile/Profile";
import SubSections from "./pages/QuestionsBank/SubSections";
import EditStaticPage from "./pages/StaticPages/EditStaticPage";
import ViewAllQuestions from "./pages/QuestionsBank/ViewAllQuestions";
import ViewStandardForm from "./pages/StandardForms/ViewStandardForm";
import StaticPages from "./pages/StaticPages/StaticPages";
import ProjectDetailPage from "./pages/Projects/ProjectDetailPage/ProjectDetailPage";
import CustomerDetailPage from "./pages/Customer/CustomerDetailPage/CustomerDetailPage";
import GroupDetailPage from "./pages/Group/GroupDetailPage/GroupDetailPage";
import AddQuestions from "./pages/AddQuestionBank/AddQuestion";
import ViewDynamicForm from "./pages/ViewDynamicForm/ViewDynamicForm";
import ViewStandardFormQuestions from "./pages/ViewStandardFormQuestions/ViewStandardFormQuestions";
import UserDetailPage from "./components/common/UserDetailPage/UserDetailPage";
import FormDetailsPage from "./components/common/UserDetailPage/FormDetailsPage";
import CustomerReports from "./pages/CustomerReports/CustomerResports";
import ContactUs from "./pages/ContactUs/ContactUs";
import LicenceDetailPage from "./pages/License/LicenseDetailPage/LicenceDetailPage";
import License from "./pages/License/License";
import NewOnBoarding from "./pages/Customer/NewOnBoarding/NewOnBoarding";
import CustomerReportsDetails from "./pages/CustomerReports/CustomerReportsDetailsPage/CustomerReportsDetails";
import CustomerImportPage from "./pages/Customer/CustomerDetailPage/CustomerImportPage";
import ImportUserPage from "./pages/Customer/CustomerDetailPage/ImportUserPage";
import Subscribers from "./pages/Subscribers/Subscribers";

function App() {
  const dispatch = useDispatch();
  const sessionExpired = useSelector((state) => state.auth.sessionExpired);
  const { isLoggedIn } = useAppInitialization();
  const renderProtectedRoute = (path, Component) => (
    <Route
      path={path}
      element={
        <ProtectedRoute access={["admin"]}>
          <Component />
        </ProtectedRoute>
      }
    />
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "Lexend, sans-serif",
        },
      }}
    >
      <Layout className="flex">
        <Toaster reverseOrder={false} />
        <ToastContainer />
        <ErrorBoundary fallbackRender={ErrorPage}>
          <div className="w-full">
            <Routes>
              <Route
                path="/dashboard"
                element={
                  isLoggedIn ? (
                    <Navigate to="/dashboard" />
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              {/* Public Routes */}
              <Route path="/login" element={<LoginAdmin />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/products" element={<EnvironmentBrsrReport />} />
              <Route path="/switch/:token" element={<SessionLogin />} />

              {/* Admin Routes */}
              <Route
                path="/*"
                element={
                  isLoggedIn ? (
                    <AdminRoutes />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              >
                {renderProtectedRoute("dashboard", Dashboard)}
                {renderProtectedRoute("projects", Projects)}
                {renderProtectedRoute(
                  "projects/project-page",
                  ProjectDetailPage,
                )}

                {renderProtectedRoute("license", License)}
                {renderProtectedRoute(
                  "license/license-detail-page",
                  LicenceDetailPage,
                )}

                {renderProtectedRoute("group", Group)}
                {renderProtectedRoute("group/group-page/:groupId", GroupDetailPage)}
                {renderProtectedRoute("customer", Customer)}
                {renderProtectedRoute("customer/new-onboarding", NewOnBoarding)}
                {renderProtectedRoute(
                  "customer/customer-page/:userId",
                  CustomerDetailPage,
                )}
                {renderProtectedRoute(
                  "customer/customer-page/:userId/import",
                  CustomerImportPage,
                )}
                {renderProtectedRoute(
                  "customer/customer-page/:userId/import-location",
                  CustomerImportPage,
                )}
                {renderProtectedRoute("subscribers", Subscribers)}
                {renderProtectedRoute(
                  "customer/customer-page/:userId/import-users",
                  ImportUserPage,
                )}
                {renderProtectedRoute("question-bank", QuestionBank)}
                {renderProtectedRoute("standard-forms", StandardForms)}
                {renderProtectedRoute("support-ticket", SupportTicket)}
                {renderProtectedRoute(
                  `support-ticket/chat/:ticketId`,
                  SupportAction,
                  [],
                )}
                {renderProtectedRoute("contact-us", ContactUs)}
                {renderProtectedRoute("customer-reports", CustomerReports)}
                {renderProtectedRoute(
                  "customer-reports/customer-report-details",
                  CustomerReportsDetails,
                )}
                {renderProtectedRoute("user-kyc", UserKYC)}
                {renderProtectedRoute("setting/users", Users)}
                {renderProtectedRoute("setting/users/user-details", UserDetailPage)}
                {renderProtectedRoute("projects/project-page/user-details", UserDetailPage)}
                {renderProtectedRoute(
                  "setting/users/user-details/form-details/:formId",
                  FormDetailsPage,
                )}
                {renderProtectedRoute("setting/project-type", ProjectType)}
                {renderProtectedRoute("setting/licence-cost", LicenceCost)}
                {renderProtectedRoute("setting/Reports-Type", ReportType)}
                {renderProtectedRoute("setting/static-pages", StaticPages)}
                {renderProtectedRoute(
                  "setting/static-pages/:id",
                  EditStaticPage,
                )}
 {renderProtectedRoute("coupons", Coupons)}
                {renderProtectedRoute("setting/roles", Roles)}
                {renderProtectedRoute("report", Report)}
                {renderProtectedRoute("profile", Profile)}
                {renderProtectedRoute("question-bank/:sectionId", SubSections)}
                {/* {renderProtectedRoute('view/:formId', ViewAll)} */}
                {renderProtectedRoute(
                  "question-bank/view-all",
                  ViewAllQuestions,
                )}

                {renderProtectedRoute(
                  "standard-forms/:formId/all-questions",
                  ViewStandardFormQuestions,
                )}
                {renderProtectedRoute(
                  "dynamic-forms/:formId/all-questions",
                  ViewDynamicForm,
                )}

                {/* {renderProtectedRoute('question-bank/:sectionId', SubSectionss)} */}
                {renderProtectedRoute("add-questions/:formId", AddQuestions)}

                {renderProtectedRoute(
                  "question-bank/sub-section/:questionId",
                  Questions,
                )}
                {renderProtectedRoute(
                  "standard-forms/:formId",
                  ViewStandardForm,
                  [],
                )}
              </Route>
              {/* Catch-all for errors */}
              <Route path="*" element={<ErrorPage />} />
            </Routes>
          </div>
        </ErrorBoundary>
      </Layout>
      {sessionExpired && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-md">
          <PopupCard
            type="warning"
            title="Session Expired"
            message="Your session has expired due to inactivity. For security reasons, please sign in again to continue using CARBURN."
            confirmText="Sign In Again"
            showConfirmOnly={true}
            confirmBtnClass="bg-[#06513D] hover:bg-[#054a36] text-white"
            onConfirm={() => {
              dispatch(logout());
              dispatch(setSessionExpired(false));
              window.location.href = "/login";
            }}
          />
        </div>
      )}
    </ConfigProvider>
  );
}

export default App;
